import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSearchParams} from '@github-ui/use-navigate'
import {createContext, use, useEffect, useMemo, useReducer, useRef, type ReactNode, type RefObject} from 'react'

import {measureIsWideViewport, DIFF_COMPARISON_VIEWER_CONTAINER_ID} from '../helpers/measure-viewport'

export const DockedPanel = {
  AgentSessions: 'agentSessions',
  Comments: 'comments',
  Alerts: 'alerts',
  Overview: 'overview',
  MergeStatus: 'mergeStatus',
} as const

export type DockedPanel = (typeof DockedPanel)[keyof typeof DockedPanel] | null

interface DockedPanelState {
  activePanel: DockedPanel
  isCompact: boolean
  renderActive: boolean
}

type DockedPanelAction =
  | {type: 'OPEN_PANEL'; panel: Exclude<DockedPanel, null>; isCompact: boolean}
  | {type: 'CLOSE_PANEL'}
  | {type: 'HIDE_FOR_RESIZE'}
  | {type: 'SHOW_ON_RESIZE'}
  | {type: 'TOGGLE_PANEL'; panel: Exclude<DockedPanel, null>; isCompact: boolean}

const initialState: DockedPanelState = {
  activePanel: null,
  isCompact: false,
  renderActive: false,
}

function dockedPanelReducer(state: DockedPanelState, action: DockedPanelAction): DockedPanelState {
  switch (action.type) {
    case 'OPEN_PANEL':
      return {activePanel: action.panel, isCompact: action.isCompact, renderActive: true}
    case 'CLOSE_PANEL':
      return initialState
    case 'HIDE_FOR_RESIZE':
      return {...state, renderActive: false}
    case 'SHOW_ON_RESIZE':
      return {...state, renderActive: true}
    case 'TOGGLE_PANEL':
      if (state.activePanel === action.panel && state.renderActive) {
        return initialState
      }
      return {activePanel: action.panel, isCompact: action.isCompact, renderActive: true}
  }
}

interface DockedPanelActions {
  openPanel: (panel: Exclude<DockedPanel, null>, returnFocusTo?: HTMLElement | null) => void
  closePanel: () => void
  togglePanel: (panel: Exclude<DockedPanel, null>, returnFocusTo?: HTMLElement | null) => void
}

export interface DockedPanelFocusRefs {
  /** Ref to the close button inside the Panel component */
  closeButtonRef: RefObject<HTMLButtonElement | null>
  /** Ref to the element that should receive focus when the panel closes */
  returnFocusRef: RefObject<HTMLElement | null>
}

// Split contexts to prevent unnecessary re-renders:
// - Components that only need to trigger actions won't re-render when panel state changes
// - Components that need to read state will subscribe to state context
export const DockedPanelStateContext = createContext<DockedPanelState>(initialState)

const defaultActions: DockedPanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

export const DockedPanelDispatchContext = createContext<DockedPanelActions>(defaultActions)

const defaultFocusRefs: DockedPanelFocusRefs = {
  closeButtonRef: {current: null},
  returnFocusRef: {current: null},
}

export const DockedPanelFocusContext = createContext<DockedPanelFocusRefs>(defaultFocusRefs)

interface DockedPanelProviderProps {
  children: ReactNode
  isOnSupportedRoute?: boolean
}

const PANEL_PARAM = 'panel'
const VALID_PANELS: ReadonlySet<string> = new Set<string>(Object.values(DockedPanel))

function parsePanelParam(value: string | null): Exclude<DockedPanel, null> | null {
  return value !== null && VALID_PANELS.has(value) ? (value as Exclude<DockedPanel, null>) : null
}

export function DockedPanelProvider({children, isOnSupportedRoute = false}: DockedPanelProviderProps) {
  const isPanelUrlParamEnabled = isFeatureEnabled('prs_url_search_param_panels')

  // useSearchParams is always called to satisfy the rules of hooks.
  // When persistence is enabled, the initial panel is read from the URL param,
  // and changes are synced back to the URL via a one-way effect.
  const [searchParams, setSearchParams] = useSearchParams()

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const [state, dispatch] = useReducer(dockedPanelReducer, initialState)

  // Restore panel from URL param after mount, when DOM elements (portal
  // targets, measurement containers) are available.  Running in the reducer
  // initializer caused SSR crashes (bare `document` access) and also ran
  // before the portal target was in the DOM, falling through to Dialog mode.
  const hasRestoredRef = useRef(false)
  useEffect(() => {
    if (hasRestoredRef.current || !isPanelUrlParamEnabled) return
    hasRestoredRef.current = true
    const panel = parsePanelParam(searchParams.get(PANEL_PARAM))
    if (panel) {
      const isWide = measureIsWideViewport()
      dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide})
    }
  }, [isPanelUrlParamEnabled, searchParams])

  // Mirror state in refs so memoized action callbacks can read values synchronously
  const stateRef = useRef(state)

  // Keep stateRef in sync with reducer state after each update.
  // Using useEffect (not render-time assignment) to satisfy React Compiler rules.
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Sync panel state → URL when persistence is enabled.
  // This runs in the provider — the effect deps don't affect the dispatch
  // context, so action-only consumers won't re-render.
  useEffect(() => {
    if (!isPanelUrlParamEnabled) return
    const currentUrlPanel = parsePanelParam(searchParams.get(PANEL_PARAM))
    if (state.activePanel === currentUrlPanel) return

    const newParams = new URLSearchParams(searchParams)
    if (state.activePanel) {
      newParams.set(PANEL_PARAM, state.activePanel)
    } else {
      newParams.delete(PANEL_PARAM)
    }
    setSearchParams(newParams)
  }, [state.activePanel, isPanelUrlParamEnabled, searchParams, setSearchParams])

  // Memoize actions object to keep dispatch context value stable
  // This ensures components using only useDockedPanelActions() won't re-render on state changes
  const actions = useMemo<DockedPanelActions>(
    () => ({
      openPanel: (panel: Exclude<DockedPanel, null>, returnFocusTo?: HTMLElement | null) => {
        if (returnFocusTo) returnFocusRef.current = returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide})
        // Only focuses the close button if the panel will be visible (wide viewport).
        // On compact viewports the panel shows as a dialog which manages its own focus.
        if (isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
      closePanel: () => {
        dispatch({type: 'CLOSE_PANEL'})
      },
      togglePanel: (panel: Exclude<DockedPanel, null>, returnFocusTo?: HTMLElement | null) => {
        if (returnFocusTo) returnFocusRef.current = returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'TOGGLE_PANEL', panel, isCompact: !isWide})
        // Focus the close button when opening/switching (not closing) in wide mode
        if (stateRef.current.activePanel !== panel && isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
    }),
    [returnFocusRef, closeButtonRef],
  )

  // Auto-close the panel when the container shrinks below the breakpoint while in docked (non-compact) mode.
  // A panel opened in compact (dialog) mode is unaffected.
  // Uses ResizeObserver on the container to detect when the side panel changes available width.
  // Falls back to observing documentElement for actual viewport resizes.
  useEffect(() => {
    function handleResize() {
      // Short circuit to avoid measuring viewport and dispatching if activePanel is not defined
      if (!stateRef.current.activePanel) return

      const isWide = measureIsWideViewport()

      if (!stateRef.current.isCompact && !isWide) {
        dispatch({type: 'HIDE_FOR_RESIZE'})
      }

      if (stateRef.current.renderActive === false && isWide) {
        dispatch({type: 'SHOW_ON_RESIZE'})
      }
    }

    const container = document.getElementById(DIFF_COMPARISON_VIEWER_CONTAINER_ID)
    let observer: ResizeObserver | null = null
    if (container) {
      observer = new ResizeObserver(handleResize)
      observer.observe(container)
    }

    return () => {
      observer?.disconnect()
    }
  }, [])

  // Stable ref object - refs don't change identity so this is safe to memoize once
  const focusRefs = useMemo<DockedPanelFocusRefs>(() => ({closeButtonRef, returnFocusRef}), [])

  // Only expose the panel state when on a supported route (files changed).
  // On other routes the panel is effectively closed, hiding the sidebar.
  const effectiveState = isOnSupportedRoute ? state : initialState

  return (
    <DockedPanelStateContext value={effectiveState}>
      <DockedPanelDispatchContext value={actions}>
        <DockedPanelFocusContext value={focusRefs}>{children}</DockedPanelFocusContext>
      </DockedPanelDispatchContext>
    </DockedPanelStateContext>
  )
}

/** Returns memoized actions to control the docked panel - stable reference, won't cause re-renders */
export function useDockedPanelActions(): DockedPanelActions {
  return use(DockedPanelDispatchContext)
}

/** Returns whether any docked panel is currently open on a supported route (files changed) */
export function useIsDockedPanelOpen(dockedPanelType?: DockedPanel | undefined): boolean {
  const context = use(DockedPanelStateContext)
  if (dockedPanelType) {
    return context.activePanel === dockedPanelType && context.renderActive
  }

  return context.activePanel !== null && context.renderActive
}

/** Returns shared focus refs for managing focus between panel openers and the close button */
export function useDockedPanelFocusRefs(): DockedPanelFocusRefs {
  return use(DockedPanelFocusContext)
}

/** Returns whether the panel is in compact mode (container < 1012px) and showing as a dialog overlay */
export function useIsDockedPanelCompact(): boolean {
  return use(DockedPanelStateContext).isCompact
}
